import * as THREE from 'three';
import { LOADING } from './constants';

/**
 * Events emitted by TextureCache
 */
export interface TextureCacheEvents {
  disposed: (url: string, texture: THREE.Texture) => void;
  evicted: (url: string, texture: THREE.Texture) => void;
}

/**
 * Texture cache statistics
 */
export interface TextureCacheStats {
  size: number;
  maxSize: number;
  totalRefs: number;
  memoryEstimate: number;
}

/**
 * Reference-counted texture cache with optional LRU eviction
 */
export class TextureCache {
  private readonly textureCache = new Map<string, THREE.Texture>();
  private readonly textureRefCount = new Map<string, number>();
  private readonly accessOrder = new Map<string, number>(); // For LRU tracking
  private accessCounter = 0;
  private readonly maxCacheSize: number;
  private readonly eventHandlers = new Map<keyof TextureCacheEvents, Set<Function>>();

  constructor(maxCacheSize: number = LOADING.DEFAULT_MAX_CACHE_SIZE) {
    this.maxCacheSize = maxCacheSize;
  }

  /**
   * Get texture from cache
   */
  get(url: string): THREE.Texture | undefined {
    const texture = this.textureCache.get(url);
    if (texture) {
      // Update access order for LRU
      this.accessOrder.set(url, ++this.accessCounter);
    }
    return texture;
  }

  /**
   * Put texture in cache
   */
  put(url: string, texture: THREE.Texture): void {
    // Tag texture with URL for reference counting
    (texture as any).__tileUrl = url;
    
    this.textureCache.set(url, texture);
    this.accessOrder.set(url, ++this.accessCounter);
    
    // Initialize ref count if not exists
    if (!this.textureRefCount.has(url)) {
      this.textureRefCount.set(url, 0);
    }

    // Evict if necessary
    this.evictIfNeeded();
  }

  /**
   * Check if texture exists in cache
   */
  has(url: string): boolean {
    return this.textureCache.has(url);
  }

  /**
   * Increment reference count for texture
   */
  incRef(url: string): void {
    const count = this.textureRefCount.get(url) || 0;
    this.textureRefCount.set(url, count + 1);
    
    // Update access order
    if (this.textureCache.has(url)) {
      this.accessOrder.set(url, ++this.accessCounter);
    }
  }

  /**
   * Decrement reference count and dispose if no longer referenced
   */
  decRef(url: string): void {
    const count = this.textureRefCount.get(url) || 0;
    const newCount = Math.max(0, count - 1);
    
    if (newCount === 0) {
      this.textureRefCount.delete(url);
      const texture = this.textureCache.get(url);
      
      if (texture) {
        this.textureCache.delete(url);
        this.accessOrder.delete(url);
        
        // Dispose texture
        texture.dispose();
        
        // Emit disposed event
        this.emit('disposed', url, texture);
      }
    } else {
      this.textureRefCount.set(url, newCount);
    }
  }

  /**
   * Get reference count for a texture
   */
  getRefCount(url: string): number {
    return this.textureRefCount.get(url) || 0;
  }

  /**
   * Force remove texture from cache (ignoring ref count)
   */
  forceRemove(url: string): boolean {
    const texture = this.textureCache.get(url);
    if (!texture) {
      return false;
    }

    this.textureCache.delete(url);
    this.textureRefCount.delete(url);
    this.accessOrder.delete(url);
    
    texture.dispose();
    this.emit('disposed', url, texture);
    
    return true;
  }

  /**
   * Evict least recently used textures if cache is over capacity
   */
  private evictIfNeeded(): void {
    if (this.textureCache.size <= this.maxCacheSize) {
      return;
    }

    // Find textures with zero references, sorted by access time (oldest first)
    const unreferencedTextures = Array.from(this.textureCache.entries())
      .filter(([url]) => (this.textureRefCount.get(url) || 0) === 0)
      .map(([url, texture]) => ({
        url,
        texture,
        accessTime: this.accessOrder.get(url) || 0,
      }))
      .sort((a, b) => a.accessTime - b.accessTime);

    // Evict oldest unreferenced textures until under capacity
    const toEvict = this.textureCache.size - this.maxCacheSize;
    const evicted = unreferencedTextures.slice(0, toEvict);

    for (const { url, texture } of evicted) {
      this.textureCache.delete(url);
      this.textureRefCount.delete(url);
      this.accessOrder.delete(url);
      
      texture.dispose();
      this.emit('evicted', url, texture);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): TextureCacheStats {
    const totalRefs = Array.from(this.textureRefCount.values()).reduce((sum, count) => sum + count, 0);
    
    // Rough memory estimate (assuming average texture size)
    const averageTextureSize = 1024 * 1024; // 1MB estimate per texture
    const memoryEstimate = this.textureCache.size * averageTextureSize;

    return {
      size: this.textureCache.size,
      maxSize: this.maxCacheSize,
      totalRefs,
      memoryEstimate,
    };
  }

  /**
   * Get all cached URLs
   */
  getCachedUrls(): string[] {
    return Array.from(this.textureCache.keys());
  }

  /**
   * Get URLs with zero references (candidates for eviction)
   */
  getUnreferencedUrls(): string[] {
    return Array.from(this.textureCache.keys()).filter(
      (url) => (this.textureRefCount.get(url) || 0) === 0
    );
  }

  /**
   * Clear all textures (respecting ref counts)
   */
  clear(force = false): void {
    if (force) {
      // Force clear everything
      for (const [url, texture] of this.textureCache.entries()) {
        texture.dispose();
        this.emit('disposed', url, texture);
      }
      
      this.textureCache.clear();
      this.textureRefCount.clear();
      this.accessOrder.clear();
    } else {
      // Only clear unreferenced textures
      const unreferencedUrls = this.getUnreferencedUrls();
      for (const url of unreferencedUrls) {
        this.forceRemove(url);
      }
    }
  }

  /**
   * Validate cache integrity (for debugging)
   */
  validate(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    
    // Check that all cached textures have ref counts
    for (const url of this.textureCache.keys()) {
      if (!this.textureRefCount.has(url)) {
        issues.push(`Texture ${url} is cached but has no ref count`);
      }
    }
    
    // Check that all ref counts have corresponding cached textures
    for (const url of this.textureRefCount.keys()) {
      if (!this.textureCache.has(url)) {
        issues.push(`URL ${url} has ref count but no cached texture`);
      }
    }
    
    // Check that access order matches cache
    for (const url of this.textureCache.keys()) {
      if (!this.accessOrder.has(url)) {
        issues.push(`Texture ${url} is cached but has no access order`);
      }
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Add event listener
   */
  on<K extends keyof TextureCacheEvents>(event: K, handler: TextureCacheEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Remove event listener
   */
  off<K extends keyof TextureCacheEvents>(event: K, handler: TextureCacheEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event
   */
  private emit<K extends keyof TextureCacheEvents>(
    event: K,
    ...args: Parameters<TextureCacheEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          (handler as any)(...args);
        } catch (error) {
          // Silently handle event errors
        }
      }
    }
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.clear(true);
    this.eventHandlers.clear();
  }
}
